import { useEffect, useRef, useState } from 'react'

export interface TooltipPosition {
  top: number
  left: number
}

export interface UseTooltipOptions {
  delay?: number
  position?: 'bottom' | 'top' | 'right'
  offset?: number
}

export function useTooltip(options: UseTooltipOptions = {}) {
  const { delay = 200, position = 'bottom', offset = 8 } = options

  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({ top: 0, left: 0 })
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (showTooltip && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()

      if (position === 'right') {
        setTooltipPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + offset,
        })
      } else if (position === 'top') {
        setTooltipPosition({
          top: rect.top - offset,
          left: rect.left + rect.width / 2,
        })
      } else {
        // bottom (default)
        setTooltipPosition({
          top: rect.bottom + offset,
          left: rect.left + rect.width / 2,
        })
      }
    }
  }, [showTooltip, position, offset])

  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current)
      }
    }
  }, [])

  const showTooltipWithDelay = () => {
    tooltipTimerRef.current = setTimeout(() => {
      setShowTooltip(true)
    }, delay)
  }

  const hideTooltip = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current)
      tooltipTimerRef.current = null
    }
    setShowTooltip(false)
  }

  return { showTooltip, tooltipPosition, buttonRef, showTooltipWithDelay, hideTooltip }
}
